/**
 * IntellioStatusStrip (§5.3)
 *
 * Compact status system showing confidence, sensitivity, proactive mode,
 * approval state, and runtime policy badges.
 *
 * State rules:
 * - Low confidence renders warning styling
 * - Sensitive state never shares neutral styling
 * - Approval-required state looks blocking, not decorative
 *
 * @module intellio-v3/IntellioStatusStrip
 */
import React from 'react';
import type { IntellioStatusItem, IntellioStatusTone } from './types.js';

export interface IntellioStatusStripProps {
  items: IntellioStatusItem[];
}

const toneClasses: Record<IntellioStatusTone, string> = {
  success: 'bg-jungle/10 text-jungle border-jungle/20',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  danger: 'bg-strawberry/10 text-strawberry border-strawberry/20',
  info: 'bg-primary/10 text-primary border-primary/20',
  neutral: 'bg-gray-100 text-irongrey border-gray-200',
};

export const IntellioStatusStrip: React.FC<IntellioStatusStripProps> = ({ items }) => {
  if (items.length === 0) return null;

  return (
    <div
      data-testid="intellio-status-strip"
      className="flex flex-wrap items-center gap-2 font-[Albert_Sans]"
    >
      {items.map((item) => (
        <span
          key={item.id}
          data-testid={`intellio-status-${item.id}`}
          className={[
            'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium',
            toneClasses[item.tone] || toneClasses.neutral,
          ].join(' ')}
        >
          {item.icon}
          <span className="font-semibold">{item.label}:</span>
          <span>{item.value}</span>
        </span>
      ))}
    </div>
  );
};
